<script setup lang="ts">
import axios from 'axios';
import { ref } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { toPowerpoint as toPowerpointPage } from '@/routes/tools/pdf';
import toPowerpointTool from '@/routes/tools/pdf/to-powerpoint';

const { lineWithMax } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.to_powerpoint.seo',
    fallbackTitleKey: 'tools.pdf.to_powerpoint.title',
    fallbackTitleLabel: 'PDF to PowerPoint',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: toPowerpointPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.to_powerpoint.title',
                title: 'PDF to PowerPoint',
                href: toPowerpointPage.url(),
            },
        ],
    },
});

type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const status = ref<Status>('idle');
const statusMessage = ref('');

function onFileSelected(files: File[]): void {
    file.value = files[0] ?? null;
    status.value = 'idle';
}

function removeFile(): void {
    file.value = null;
    status.value = 'idle';
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        const text = await (e.response.data as Blob).text();

        try {
            const parsed = JSON.parse(text);
            const messages = Object.values(parsed.errors ?? {}).flat();

            return (
                (messages[0] as string) ||
                t('tools.pdf.ui.conversion_failed', 'Conversion failed.')
            );
        } catch {
            return t('tools.pdf.ui.conversion_failed', 'Conversion failed.');
        }
    }

    return t('tools.pdf.ui.conversion_failed', 'Conversion failed.');
}

async function convert(): Promise<void> {
    if (!file.value || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t(
        'tools.pdf.ui.converting_to_powerpoint',
        'Converting to PowerPoint...',
    );

    const formData = new FormData();
    formData.append('file', file.value);

    try {
        const response = await axios.post(
            toPowerpointTool.submit.url(),
            formData,
            {
                responseType: 'blob',
            },
        );

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'converted.pptx';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.converted_success',
            'Converted successfully!',
        );
        file.value = null;
    } catch (err: unknown) {
        status.value = 'error';
        const msg = await extractError(err);
        statusMessage.value = msg.includes('LibreOffice')
            ? t(
                  'tools.pdf.ui.libreoffice_missing',
                  'LibreOffice is not installed on the server. This tool requires LibreOffice.',
              )
            : msg;
    }
}
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-to-powerpoint" />

    <ToolLayout
        :title="t('tools.pdf.to_powerpoint.title', 'PDF to PowerPoint')"
        :subtitle="t('tools.pdf.to_powerpoint.description', '')"
    >
        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <DropZone
                    accept="application/pdf"
                    :multiple="false"
                    :label="
                        t(
                            'tools.pdf.ui.drop_pdf_here',
                            'Click or drag PDF here',
                        )
                    "
                    :sublabel="
                        lineWithMax(
                            t('tools.pdf.ui.pdf_files_only', 'PDF files only'),
                        )
                    "
                    @update:model-value="onFileSelected"
                />

                <div v-if="file" class="mt-4">
                    <FileCard
                        :file="file"
                        :show-drag-handle="false"
                        @remove="removeFile"
                    />
                </div>
            </div>

            <Button
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="!file || status === 'loading'"
                @click="convert"
            >
                {{
                    t(
                        'tools.pdf.ui.convert_to_powerpoint',
                        'Convert to PowerPoint',
                    )
                }}
            </Button>

            <StatusMessage :status="status" :message="statusMessage" />
        </div>
    </ToolLayout>
</template>
